import { Check, ChevronDown, ChevronUp, Hourglass } from 'lucide-react';
import { Spinner } from 'design-system/react';

import { type TaskResponse, TeamResponse } from '../../client';
import { useIsTaskMutating, useRequireFeatureFlagIfProvided, useTasksSortingOption } from '../../hooks';
import type { TasksSortingOption } from '../../models';
import { sortTasks } from '../../sorting/sortTasks';

import { FeatureBadge, MiniGameIcon, NoTasksAlert, SizeBadge } from '../atoms';

type SimulationState = 'loading' | 'not-started' | 'running' | 'paused' | 'finished';

interface Props {
  team: TeamResponse;
  tasks: TaskResponse[];
  onTaskClick: (task: TaskResponse) => void;
  simulationState: SimulationState;
}

export function TasksList({ team, tasks, onTaskClick, simulationState }: Props) {
  const showFeature = !!useRequireFeatureFlagIfProvided('show_feature_badge');
  const enableSorting = !!useRequireFeatureFlagIfProvided('allow_task_sorting');
  const { sortTasksBy, setSortTasksBy } = useTasksSortingOption();

  const pendingTeamTasks = tasks.filter((task) => task.team_name === team.name && task.status !== 'completed');
  const completedTeamTasks = tasks.filter((task) => task.team_name === team.name && task.status === 'completed');
  const otherTeamsTasks = tasks.filter((task) => task.team_name !== team.name);

  const showSimulationNotStartedAlert = tasks.length === 0 && simulationState === 'not-started';
  const showSimulationPausedAlert = simulationState === 'paused' && tasks.length === 0;
  const showNoTasksAlert =
    tasks.length > 0 &&
    pendingTeamTasks.length === 0 &&
    simulationState !== 'not-started' &&
    simulationState !== 'paused';

  const teamHasTasks = completedTeamTasks.length > 0;
  const allTasksCompleted = tasks.length > 0 && tasks.every((task) => task.status === 'completed');

  let noTasksTitle: string;
  let noTasksMessage: string;
  if (!teamHasTasks) {
    noTasksTitle = 'All done!';
    noTasksMessage = "There's no work for your team in this feature.";
  } else if (allTasksCompleted) {
    noTasksTitle = 'Feature complete!';
    noTasksMessage = 'All tasks have been completed. This feature is now delivering value!';
  } else {
    noTasksTitle = 'Your work is done!';
    noTasksMessage = 'Your team has completed all tasks. Other teams still have work to do on this feature.';
  }

  const isSimulationActive = simulationState === 'running';

  return (
    <div className="w-full">
      <div className="flex flex-col">
        {showSimulationNotStartedAlert && (
          <NoTasksAlert
            className="mt-[20vh]"
            title={"You're all set!"}
            message={'Stay tuned, we will start the simulation soon.'}
          />
        )}
        {showSimulationPausedAlert && (
          <NoTasksAlert
            className="mt-[20vh]"
            title={'Simulation Paused'}
            message={'The simulation is currently paused. You can review tasks but cannot work on them.'}
          />
        )}
        {showNoTasksAlert && <NoTasksAlert className="mt-3" title={noTasksTitle} message={noTasksMessage} />}
        {simulationState === 'paused' && tasks.length > 0 && (
          <NoTasksAlert
            className="mt-3"
            title={'Simulation Paused'}
            message={'The simulation is currently paused. You can review tasks below but cannot work on them.'}
          />
        )}
        <TasksSection
          title={`${team.name} team' tasks`}
          tasks={enableSorting ? sortTasks(pendingTeamTasks, sortTasksBy) : pendingTeamTasks}
          onTaskClick={onTaskClick}
          inactive={!isSimulationActive}
          showFeature={showFeature}
          enableSorting={enableSorting}
          currentSort={sortTasksBy}
          onSort={setSortTasksBy}
        />
        <TasksSection
          title={'Completed'}
          tasks={enableSorting ? sortTasks(completedTeamTasks, sortTasksBy) : completedTeamTasks}
          inactive
          showFeature={showFeature}
          enableSorting={enableSorting}
          currentSort={sortTasksBy}
          onSort={setSortTasksBy}
        />
        <TasksSection
          title={"Other teams' tasks"}
          tasks={enableSorting ? sortTasks(otherTeamsTasks, sortTasksBy) : otherTeamsTasks}
          inactive
          showFeature={showFeature}
          showTeam
          enableSorting={enableSorting}
          currentSort={sortTasksBy}
          onSort={setSortTasksBy}
        />
      </div>
    </div>
  );
}

// ─── Column definitions ──────────────────────────────────────────────────────

interface ColumnDef {
  key: string;
  label: string;
  sortOption?: TasksSortingOption;
  defaultDirection: 'asc' | 'desc';
  gridWidth: string;
  align?: 'left' | 'right' | 'center';
}

const STATUS_COL: ColumnDef = {
  key: 'status',
  label: '',
  defaultDirection: 'asc',
  gridWidth: '1.5rem',
  align: 'center',
};

const TASK_COL: ColumnDef = {
  key: 'task',
  label: 'Task',
  sortOption: 'task-id',
  defaultDirection: 'asc',
  gridWidth: '1fr',
  align: 'left',
};

const TEAM_COL: ColumnDef = {
  key: 'team',
  label: 'Team',
  defaultDirection: 'asc',
  gridWidth: 'minmax(4rem, auto)',
  align: 'left',
};

const FEATURE_COL: ColumnDef = {
  key: 'feature',
  label: 'Feature',
  sortOption: 'feature',
  defaultDirection: 'asc',
  gridWidth: 'minmax(4rem, auto)',
  align: 'left',
};

const SIZE_COL: ColumnDef = {
  key: 'size',
  label: 'Size',
  sortOption: 'size',
  defaultDirection: 'desc',
  gridWidth: 'minmax(3rem, auto)',
  align: 'left',
};

const GAME_COL: ColumnDef = {
  key: 'game',
  label: 'Game',
  sortOption: 'game',
  defaultDirection: 'asc',
  gridWidth: '2rem',
  align: 'center',
};

// ─── Sort helpers ────────────────────────────────────────────────────────────

function getReverse(option: TasksSortingOption): TasksSortingOption {
  if (option.endsWith('-desc')) {
    return option.replace(/-desc$/, '') as TasksSortingOption;
  }
  return `${option}-desc` as TasksSortingOption;
}

function getBaseOption(option: TasksSortingOption): string {
  return option.replace(/-desc$/, '');
}

// ─── ColumnHeader ────────────────────────────────────────────────────────────

interface ColumnHeaderProps {
  column: ColumnDef;
  sortable: boolean;
  currentSort: TasksSortingOption;
  onSort: (option: TasksSortingOption) => void;
}

function ColumnHeader({ column, sortable, currentSort, onSort }: ColumnHeaderProps) {
  if (!column.label) {
    return <div />;
  }

  const isActive = column.sortOption && getBaseOption(currentSort) === getBaseOption(column.sortOption);
  const isDescending = currentSort.endsWith('-desc');

  const handleClick = () => {
    if (!sortable || !column.sortOption) return;

    if (isActive) {
      onSort(getReverse(currentSort));
    } else {
      const base = column.sortOption;
      onSort(column.defaultDirection === 'desc' ? (`${base}-desc` as TasksSortingOption) : base);
    }
  };

  const alignClass =
    column.align === 'right'
      ? 'justify-end text-right'
      : column.align === 'center'
        ? 'justify-center text-center'
        : 'justify-start text-left';

  return (
    <div
      className={`flex items-center gap-1 px-1 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide select-none ${alignClass} ${
        sortable && column.sortOption ? 'cursor-pointer hover:text-text-primary transition-colors' : ''
      }`}
      onClick={handleClick}
    >
      <span>{column.label}</span>
      {isActive && (
        <span className="text-text-accent">
          {isDescending ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
        </span>
      )}
    </div>
  );
}

// ─── TasksSection ─────────────────────────────────────────────────────────────

interface TasksSectionProps {
  title: string;
  tasks: TaskResponse[];
  onTaskClick?: (task: TaskResponse) => void;
  inactive?: boolean;
  showFeature: boolean;
  showTeam?: boolean;
  enableSorting: boolean;
  currentSort: TasksSortingOption;
  onSort: (option: TasksSortingOption) => void;
}

function TasksSection({
  title,
  tasks,
  onTaskClick,
  inactive = false,
  showFeature,
  showTeam = false,
  enableSorting,
  currentSort,
  onSort,
}: TasksSectionProps) {
  if (tasks.length === 0) {
    return null;
  }

  const columns: ColumnDef[] = [STATUS_COL, TASK_COL];
  if (showTeam) columns.push(TEAM_COL);
  if (showFeature) columns.push(FEATURE_COL);
  columns.push(SIZE_COL, GAME_COL);

  const gridTemplateColumns = columns.map((c) => c.gridWidth).join(' ');

  return (
    <>
      <p className={`mt-8 font-bold ${inactive ? 'text-text-muted' : 'text-text-primary'}`}>{title}:</p>
      <div className="grid" style={{ gridTemplateColumns }}>
        {/* Column headers */}
        {columns.map((col) => (
          <ColumnHeader key={col.key} column={col} sortable={enableSorting} currentSort={currentSort} onSort={onSort} />
        ))}
        <div className="col-span-full border-b border-border-muted" />

        {/* Data rows */}
        {tasks.map((task) => (
          <TaskItem
            key={task.id}
            task={task}
            onClick={inactive ? undefined : onTaskClick}
            inactive={inactive}
            showFeature={showFeature}
            showTeam={showTeam}
          />
        ))}
      </div>
    </>
  );
}

// ─── TaskItem ─────────────────────────────────────────────────────────────────

interface TaskProps {
  task: TaskResponse;
  onClick?: (task: TaskResponse) => void;
  inactive?: boolean;
  showFeature: boolean;
  showTeam?: boolean;
}

function TaskItem({ task, onClick, inactive = false, showFeature, showTeam = false }: TaskProps) {
  const isUpdating = useIsTaskMutating(task.id);
  const isClickable = !!onClick && !isUpdating;
  const isCompleted = task.status === 'completed';

  const handleClick = () => {
    if (isClickable) {
      onClick!(task);
    }
  };

  const cellBase = `py-2.5 border-b border-border-muted flex items-center transition-colors ${
    isClickable ? 'group-hover:bg-bg-subtle cursor-pointer' : ''
  }`;
  const textColor = isUpdating ? 'text-text-disabled' : inactive || isCompleted ? 'text-text-muted' : '';

  return (
    <div className="contents group">
      {/* Status */}
      <div className={`${cellBase} justify-center`} onClick={handleClick}>
        {task.status !== 'new' &&
          (isCompleted ? (
            <Check className="w-5 h-5 text-feedback-success" />
          ) : (
            <Hourglass className="w-5 h-5 text-feedback-warning" />
          ))}
      </div>

      {/* Task ID */}
      <div className={`${cellBase} px-1 ${textColor}`} onClick={handleClick}>
        <samp className={isCompleted ? 'line-through' : ''}>{task.id}</samp>
        {isUpdating && <Spinner size="sm" className="inline-block ml-2 align-middle" />}
      </div>

      {/* Team (conditional) */}
      {showTeam && (
        <div className={`${cellBase} px-1 text-sm ${textColor}`} onClick={handleClick}>
          {task.team_name}
        </div>
      )}

      {/* Feature (conditional) */}
      {showFeature && (
        <div className={`${cellBase} px-1`} onClick={handleClick}>
          <FeatureBadge featureId={task.feature_id} />
        </div>
      )}

      {/* Size */}
      <div className={`${cellBase} px-1`} onClick={handleClick}>
        <SizeBadge size={task.total_effort} />
      </div>

      {/* Game */}
      <div className={`${cellBase} justify-center`} onClick={handleClick}>
        <MiniGameIcon task={task} />
      </div>
    </div>
  );
}
